/**
 * Choose Your Region Modal component.
 *
 * @author Vladimir Shishlyannikov
 * @created 2024/09/12
 * @copyright 2024-present iWoo Ltd. All rights reserved.
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import * as Sentry from "@sentry/browser";
// import Cookies from "js-cookie";
import { useAppState } from "../../state";
import { useRegion, getRegion } from "../../context/RegionProvider";

// Components
import Button from "../Button/Button";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

// Icons
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Images
import Image from "./ChooseYourCityImage";

const Checkbox = ({ children, className, id, name, value, onChange }) => {
  const handleChange = React.useCallback(
    (e) => {
      onChange && onChange(!value);
    },
    [value]
  );

  return (
    <div className={className}>
      <input
        id={id}
        type="checkbox"
        name={name}
        disabled={false}
        className="tb-checkbox-new_"
        checked={value}
        onChange={handleChange}
      />
      <label
        htmlFor={id}
        tabIndex="0"
        className="tbk-text-small tbk-mb-0 tbk-flex tbk-h-3 tbk-items-center tbk-pl-4 tbk-text-primary"
      >
        {children}
      </label>
    </div>
  );
};

const SelectOption = ({
  id,
  className,
  disabled,
  checked,
  text,
  value,
  onChange,
}) => {
  const handleChange = useCallback(() => {
    onChange && onChange(value);
  }, [value, onChange]);

  return (
    <div className={`${className} tbk-relative tbk-flex tbk-p-1`}>
      <input
        type="radio"
        id={`option_${id}`}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
      />
      <label
        className="tbk-text-main tbk-w-7 tbk-grow tbk-pl-5 tbk-text-primary"
        htmlFor={`option_${id}`}
      >
        {text}
      </label>
    </div>
  );
};

// Not used
function getStoredRegion() {
  return localStorage.getItem("userRegion");
}

const selectedRegion = getRegion();

export const CityPickerLS = ({ city, isOpen, onClose, onCityChange }) => {
  const { theme } = useTheme();
  const { setRegion } = useRegion();
  const { user, updateCustomerInfo } = useAppState();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allLocationsCheckbox, setAllLocationsCheckbox] = useState(!city);
  const [laCheckbox, setLaCheckbox] = useState(city === "LOS_ANGELES");
  const [nyCheckbox, setNyCheckbox] = useState(city === "NEW_YORK");
  const [miaCheckbox, setMiaCheckbox] = useState(city === "MIAMI");

  const modalResolveRef = useRef();

  const openModal = () => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;
      setIsModalOpen(true);
    });
  };

  const closeModal = () => {
    if (modalResolveRef.current) {
      modalResolveRef.current();
      modalResolveRef.current = null;
      onClose && onClose();
    }
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      openModal();
    } else {
      closeModal();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!user) return;
    if (user.city === "LOS_ANGELES") {
      setAllLocationsCheckbox(false);
      setNyCheckbox(false);
      setMiaCheckbox(false);
      setLaCheckbox(true);
    } else if (user.city === "NEW_YORK") {
      setAllLocationsCheckbox(false);
      setNyCheckbox(true);
      setMiaCheckbox(false);
      setLaCheckbox(false);
    } else if (user.city === "MIAMI") {
      setAllLocationsCheckbox(false);
      setNyCheckbox(false);
      setMiaCheckbox(true);
      setLaCheckbox(false);
    } else {
      setAllLocationsCheckbox(true);
      setNyCheckbox(false);
      setMiaCheckbox(false);
      setLaCheckbox(false);
    }
  }, [user]);

  const handleAllLocationsCheckboxChange = () => {
    setAllLocationsCheckbox(true);
    setLaCheckbox(false);
    setNyCheckbox(false);
    setMiaCheckbox(false);
  };
  const handleLaCheckboxChange = () => {
    setAllLocationsCheckbox(false);
    setLaCheckbox(true);
    setNyCheckbox(false);
    setMiaCheckbox(false);
  };
  const handleNyCheckboxChange = () => {
    setAllLocationsCheckbox(false);
    setLaCheckbox(false);
    setNyCheckbox(true);
    setMiaCheckbox(false);
  };
  const handleMiaCheckboxChange = () => {
    setAllLocationsCheckbox(false);
    setLaCheckbox(false);
    setNyCheckbox(false);
    setMiaCheckbox(true);
  };

  const handleContinueClick = useCallback(() => {
    let cityChoice = laCheckbox
      ? "LOS_ANGELES"
      : nyCheckbox
      ? "NEW_YORK"
      : miaCheckbox
      ? "MIAMI"
      : undefined; // undefined если ALL

    if (city !== cityChoice) {
      if (user && cityChoice) {
        updateCustomerInfo({ city: cityChoice }, user.id);
      }
      onCityChange(cityChoice);
    }

    closeModal();
  }, [user, laCheckbox, nyCheckbox, miaCheckbox, city]);

  const defaultCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? {
          ...modalStyles.newMobileDefault,
          overlay: { ...modalStyles.newMobileDefault.overlay, zIndex: 1060 },
        }
      : {
          ...modalStyles.default,
          overlay: { ...modalStyles.default.overlay, zIndex: 1060 },
          content: {
            ...modalStyles.default.content,
            width: "698px",
            padding: "32px",
          },
        };

  return (
    <Modal
      appElement={document.getElementById("#app")}
      isOpen={isModalOpen}
      style={defaultCustomStyle}
      contentLabel="Choose Your City"
    >
      <div className="choose-your-region-modal tbk-rounded-lg tbk-bg-basic-white">
        <div className="tbk-items-center tbk-justify-center md:tbk-flex">
          <div className="tbk-text-center">
            <Image className="tbk-inline-block" />
          </div>
          <div className="tbk-px-4 md:tbk-pl-4 md:tbk-pr-4">
            <h2 className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-1 tbk-text-primary lg:tbk-whitespace-nowrap lg:!tbk-leading-tight">
              Choose Your City
            </h2>
            <p className="tbk-text-main tbk-mb-2 tbk-text-blue-grey">
              Select your preferred location to see events nearby.
            </p>

            <div className="tbk-mb-4">
              <SelectOption
                id={"ALL"}
                className="tbk-mb-0.5"
                text={"All Locations"}
                value={undefined}
                checked={allLocationsCheckbox}
                onChange={handleAllLocationsCheckboxChange}
              />

              <SelectOption
                id={"LOS_ANGELES"}
                className="tbk-mb-0.5"
                text={"Los Angeles"}
                value={"LOS_ANGELES"}
                checked={laCheckbox}
                onChange={handleLaCheckboxChange}
              />

              <SelectOption
                id={"NEW_YORK"}
                className="tbk-mb-0.5"
                text={"New York"}
                value={"NEW_YORK"}
                checked={nyCheckbox}
                onChange={handleNyCheckboxChange}
              />

              <SelectOption
                id={"MIAMI"}
                className="tbk-mb-0.5"
                text={"Miami"}
                value={"MIAMI"}
                checked={miaCheckbox}
                onChange={handleMiaCheckboxChange}
              />
            </div>

            <Button
              variant={"secondary"}
              className="tbk-w-full"
              endIcon={<ArrowRightIcon />}
              onClick={handleContinueClick}
            >
              Ok
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
